import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import Swal from 'sweetalert2'
import { useTranslation } from 'react-i18next'

import { useModal } from '@/contexts/ModalContext'

import { onUncaughtError } from '@/services/userAlerts'
import { ENDPOINTS, sendPostRequest } from '@/services/api'

import { i18n } from '@/translate/i18n'

import * as S from './styles'

import { CollectionType } from '@/types/collection'

interface ChildrenPath {
  value: string
  label: string
}

type ChildrenPaths = ChildrenPath[]

interface SelectedFolder {
  value: string
  label: string
  name: string
  paths: string[]
}

export interface MoveCollectionModalConfig {
  name: string
  collectionId: string
  collection: CollectionType
  allCollections: CollectionType[]
  setAction: (action: boolean) => void
  action: boolean
  setLoading: (loading: boolean) => void
}

export interface MoveCollectionModalProps {
  config: MoveCollectionModalConfig
}

const MoveCollectionModal = ({ config }: MoveCollectionModalProps) => {
  const { closeModal } = useModal()
  const router = useRouter()

  const { t } = useTranslation()

  const [moveText, setMoveText] = useState<string>()
  const [labelText, setLabelText] = useState<string>()
  const [placeholderText, setPlaceholderText] = useState<string>()
  const [cancelText, setCancelText] = useState<string>()
  const [myCollectionText, setMyCollectionText] = useState<string>()

  const [selectedFolder, setSelectedFolder] = useState<SelectedFolder>()
  const [foldersList, setFoldersList] = useState<ChildrenPaths>()
  const [showList, setShowList] = useState(false)
  const [currentItem, setCurrentItem] = useState<string>()

  useEffect(() => {
    setMoveText(`${t('buttons.move')}`)
    setLabelText(`${t('labels.moveCollection')}`)
    setPlaceholderText(`${t('placeholders.moveCollection')}`)
    setCancelText(`${t('buttons.cancel')}`)
    setMyCollectionText(`${t('labels.myCollection')}`)
  }, [t])

  const getChildren = (
    fullList: CollectionType[],
    paths: ChildrenPaths,
    currentPath: string = ''
  ) => {
    for (let i = 0; i < fullList.length; i++) {
      if (
        fullList[i].type === 'folder' &&
        fullList[i].id !== config.collectionId
      ) {
        paths.push({
          value: fullList[i].id,
          label: currentPath
            ? `${currentPath}/${fullList[i].name}`
            : fullList[i].name
        })
        if (fullList[i].child && fullList[i].id !== config.collectionId) {
          getChildren(
            fullList[i].child,
            paths,
            currentPath
              ? `${currentPath}/${fullList[i].name}`
              : fullList[i].name
          )
        }
      }
    }
  }

  useEffect(() => {
    const paths: ChildrenPaths = []
    getChildren(config.allCollections, paths)

    const removeCurrentFolder = paths.filter(item => {
      return (
        item.value !== config.collection.id &&
        item.value !== config.collection.folder_id
      )
    })

    setFoldersList(removeCurrentFolder)
  }, [])

  const onSuccess = () => {
    config.setAction(!config.action)
    Swal.fire({
      icon: 'success',
      title: i18n.t('swal.success'),
      html: i18n.t('messages.collectionMoved')
    })
  }

  const moveCollection = async (e: React.MouseEvent) => {
    e.preventDefault()
    config.setLoading(true)
    closeModal()

    const body = {
      id: config.collectionId,
      folder_id: selectedFolder.value
    }

    try {
      await sendPostRequest(ENDPOINTS.move_collection, body)
      onSuccess()
    } catch (error) {
      const msg = error.data || error
      console.log(msg)
      onUncaughtError()
    } finally {
      config.setLoading(false)
    }
  }

  const selectFolder = (value: SelectedFolder) => {
    setSelectedFolder(value)
    setCurrentItem(value.label)
  }

  return (
    config && (
      <S.MoveCollectionContainer>
        <S.MoveCollectionTitle>
          <p>
            {moveText} {config.name}
          </p>
          <button id="btn_closemodal_move" type="button" onClick={closeModal}>
            <span className="material-icons">close</span>
          </button>
        </S.MoveCollectionTitle>

        <S.MoveCollectionContent>
          <p className="label">{labelText}</p>
          <S.MoveCollectionButton
            className="selected"
            onClick={() => setShowList(!showList)}
            id="show_list_toggle"
          >
            <p>{selectedFolder ? selectedFolder.name : placeholderText}</p>
            <span id="modal-icon" className="material-icons">
              expand_more
            </span>
          </S.MoveCollectionButton>
          {showList && (
            <S.MoveCollectionListWrapper>
              <ul>
                {router.asPath !== '/Collections' && (
                  <li
                    onClick={() =>
                      selectFolder({
                        value: '',
                        label: myCollectionText,
                        name: myCollectionText,
                        paths: [myCollectionText]
                      })
                    }
                    id="select_path"
                  >
                    {myCollectionText}
                  </li>
                )}
                {foldersList.map(item => {
                  const folderPaths = item.label.split(`/`)
                  const folderName = folderPaths[folderPaths.length - 1]
                  return (
                    <li
                      key={item.value}
                      onClick={() =>
                        selectFolder({
                          ...item,
                          name: folderName,
                          paths: folderPaths
                        })
                      }
                      id="select_path"
                    >
                      {folderName}
                    </li>
                  )
                })}
              </ul>
              {currentItem && (
                <p>
                  {selectedFolder.paths.map((item, idx) => (
                    <span key={`${item}-${idx}`}>{` / ${item}`}</span>
                  )) || myCollectionText}
                </p>
              )}
            </S.MoveCollectionListWrapper>
          )}
          <div className="buttons">
            <button type="button" onClick={closeModal} id="btn_cancel_move">
              {cancelText}
            </button>
            <button
              type="submit"
              onClick={moveCollection}
              disabled={!selectedFolder}
              id={'btn_move'}
            >
              {moveText}
            </button>
          </div>
        </S.MoveCollectionContent>
      </S.MoveCollectionContainer>
    )
  )
}

export default MoveCollectionModal
